// Builds the full list of revisions made to a Transfer Order / Sales Order
// for the Revision History dialog. Revisions saved since the audit trail was
// added carry a `revision_history` entry; older ones only left a snapshot of
// the prior version in `revisions`, so those rows are rebuilt by comparing
// each snapshot with the version that replaced it.
import { revisionNo } from "./revisionNo";

const totalQty = (lines) => (lines || []).reduce((s, l) => s + Number(l?.qty || 0), 0);

const latestDate = (lines, fallback) => {
  let best = null;
  for (const l of lines || []) {
    const t = l?.delivery_date ? new Date(l.delivery_date).getTime() : NaN;
    if (!Number.isNaN(t) && (best === null || t > new Date(best).getTime())) best = l.delivery_date;
  }
  return best || fallback || null;
};

const orderDate = (state) => latestDate(state?.lines, state?.delivery_date || state?.expected_delivery_date);

const qtyChange = (a, b) => (b > a ? "increase" : b < a ? "decrease" : "unchanged");

const lineChanges = (oldLines, newLines) => {
  const used = new Array((oldLines || []).length).fill(false);
  const out = (newLines || []).map((ln) => {
    const i = (oldLines || []).findIndex((o, k) => !used[k] && o.item_id === ln.item_id);
    const old = i >= 0 ? oldLines[i] : null;
    if (i >= 0) used[i] = true;
    return {
      part_code: ln.part_code || old?.part_code || "",
      previous_qty: old ? Number(old.qty || 0) : null,
      revised_qty: Number(ln.qty || 0),
      previous_delivery_date: old?.delivery_date || null,
      revised_delivery_date: ln.delivery_date || null,
    };
  });
  (oldLines || []).forEach((o, k) => {
    if (!used[k]) out.push({ part_code: o.part_code || "", previous_qty: Number(o.qty || 0), revised_qty: 0,
      previous_delivery_date: o.delivery_date || null, revised_delivery_date: null, rule: "removed" });
  });
  return out;
};

/** Newest-first list of revision rows for `doc`, numbered from `baseNo`. */
export function buildRevisionTimeline(doc, baseNo) {
  if (!doc) return [];
  const n = Number(doc.revision_number || 0);
  const byRev = {};
  for (const h of doc.revision_history || []) byRev[h.revision_number] = h;
  // snapshot of the version *before* revision k is the one with revision_number k-1
  const snaps = {};
  for (const s of doc.revisions || []) snaps[Number(s.revision_number || 0)] = s;

  const rows = [];
  for (let k = 1; k <= n; k += 1) {
    const h = byRev[k];
    if (h) {
      rows.push({ ...h, revision_no: h.revision_no || revisionNo(baseNo, k), original_no: h.original_no || baseNo });
      continue;
    }
    const before = snaps[k - 1];
    const after = k === n ? doc : snaps[k];
    if (!before) {
      rows.push({ revision_number: k, revision_no: revisionNo(baseNo, k), original_no: baseNo, lines: [], incomplete: true });
      continue;
    }
    const prevQty = totalQty(before.lines);
    const newQty = after ? totalQty(after.lines) : null;
    rows.push({
      revision_number: k,
      revision_no: revisionNo(baseNo, k),
      original_no: baseNo,
      previous_qty: prevQty,
      revised_qty: newQty,
      qty_change: newQty === null ? null : qtyChange(prevQty, newQty),
      previous_delivery_date: orderDate(before),
      revised_delivery_date: after ? orderDate(after) : null,
      revised_at: before.superseded_at || (k === n ? doc.revised_at : null),
      revised_by: before.superseded_by || (k === n ? doc.revised_by : ""),
      reason: before.reason || "",
      lines: after ? lineChanges(before.lines, after.lines) : [],
    });
  }
  return rows.reverse();
}
